/**
 * Whitelist aligned with server AgendaInput / UpdateAgendaInput.
 * Excludes cobranza management and read-only fields loaded by FIND_AGENDA.
 */
const AGENDA_MUTATION_SCALAR_FIELDS = [
  "name",
  "rut",
  "address",
  "zone",
  "activity",
  "defaultCashflowArea",
  "defaultCashflowAreaClient",
  "defaultCashflowAreaProvider",
  "defaultEERRAreaClient",
  "defaultEERRAreaProvider",
  "defaultCostCenterClient",
  "defaultCostCenterProvider",
  "defaultEOAFAreaClient",
  "defaultEOAFAreaProvider",
  "nominasLabel",
  "collectionLabel",
  "email",
  "secondaryEmails",
  "collectionEmails",
  "isClient",
  "isProvider",
  "bankId",
  "bankAccount",
  "bankRut",
  "bankAccountType",
  "worldWide",
  "paymentDays",
  "paymentDaysClient",
  "paymentDaysProvider",
  "codeAccountAuxiliar",
  "codeAccount",
  "codeAccountAuxiliarClient",
  "codeAccountClient",
  "codeAccountAuxiliarProvider",
  "codeAccountProvider",
  "outsideProviderId",
  "overWrite",
];

const BANK_ACCOUNT_FIELDS = [
  "bankAccountType",
  "bankAccount",
  "bankId",
  "bankRut",
  "email",
  "isMain",
  "id",
];

const CONTACT_INFO_FIELDS = ["firstname", "lastname", "phone"];

function sanitizeAgendaBankRut(bankRut, worldWide = "cl") {
  if (bankRut === undefined || bankRut === null || bankRut === "") {
    return bankRut;
  }
  if (worldWide === "cl") {
    return String(bankRut).replace(/[^0-9Kk]/g, "");
  }
  return String(bankRut).replace(/[^0-9A-Za-z]/g, "");
}

function sanitizeBankAccounts(bankAccounts, worldWide = "cl") {
  if (!Array.isArray(bankAccounts)) return undefined;

  const sanitized = bankAccounts
    .map((account) => {
      if (!account || typeof account !== "object") return null;

      const entry = {};
      BANK_ACCOUNT_FIELDS.forEach((field) => {
        if (account[field] !== undefined) {
          entry[field] =
            field === "bankRut"
              ? sanitizeAgendaBankRut(account[field], worldWide)
              : account[field];
        }
      });

      return Object.keys(entry).length > 0 ? entry : null;
    })
    .filter(Boolean);

  return sanitized.length > 0 ? sanitized : [];
}

function sanitizeContactInfo(contactInfo) {
  if (!contactInfo || typeof contactInfo !== "object") return undefined;

  const entry = {};
  CONTACT_INFO_FIELDS.forEach((field) => {
    if (contactInfo[field] !== undefined) {
      entry[field] = contactInfo[field];
    }
  });

  return Object.keys(entry).length > 0 ? entry : undefined;
}

function sanitizeCollectionEmailContacts(contacts) {
  if (!Array.isArray(contacts)) return undefined;

  const sanitized = contacts
    .map((contact) => ({
      email: String(contact?.email || "").trim(),
      contactName: String(contact?.contactName || "").trim(),
    }))
    .filter((contact) => contact.email);

  return sanitized;
}

/**
 * Builds a GraphQL-safe AgendaInput / UpdateAgendaInput payload from form state.
 */
export function buildAgendaMutationInput(raw = {}) {
  const input = {};
  const worldWide = raw.worldWide ?? "cl";

  AGENDA_MUTATION_SCALAR_FIELDS.forEach((field) => {
    if (raw[field] !== undefined) {
      input[field] =
        field === "bankRut"
          ? sanitizeAgendaBankRut(raw[field], worldWide)
          : raw[field];
    }
  });

  const collectionEmailContacts = sanitizeCollectionEmailContacts(
    raw.collectionEmailContacts,
  );
  if (collectionEmailContacts !== undefined) {
    input.collectionEmailContacts = collectionEmailContacts;
  }

  const contactInfo = sanitizeContactInfo(raw.contactInfo);
  if (contactInfo !== undefined) {
    input.contactInfo = contactInfo;
  }

  if (raw.bankAccounts !== undefined) {
    input.bankAccounts = sanitizeBankAccounts(raw.bankAccounts, worldWide);
  }

  if (raw.labels !== undefined) {
    input.labels = Array.isArray(raw.labels) ? raw.labels : [];
  }

  return input;
}
